import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { AudioPlayer, Badge, Button, Textarea, TimelineBar } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { Pencil, Voicemail } from "lucide-react";
import { formatDuration } from "@tellescope/utils";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

export interface VoicemailContextCardProps {
  call: TellescopeModelFromName<"phone_calls">;
}

export function VoicemailContextCard({ call }: VoicemailContextCardProps) {
  const { t } = useTranslation("tickets");
  const [editingNote, setEditingNote] = useState(false);
  const noteTextareaRef = useRef<HTMLTextAreaElement>(null);

  const form = useForm({
    fields: {
      note: {
        type: "textarea",
        initialValue: call.note,
      },
    },
  });

  const phoneCallEditMutation = useTellescopeMutation({
    model: "phone_calls",
    method: "updateOne",

    onSuccess: () => {
      setEditingNote(false);
    },
    onError: () => {
      toast.error(t("Error saving note. Please try again."));
    },
  });

  const duration = useMemo(
    () => formatDuration(call.recordingDurationInSeconds ?? 0, "seconds"),
    [call.recordingDurationInSeconds]
  );

  const focusTextarea = () => {
    noteTextareaRef.current?.focus();
  };

  const handleSave = () => {
    if (form.values.note !== call.note) {
      phoneCallEditMutation.mutate({
        id: call.id,
        note: form.values.note,
      });
    } else {
      setEditingNote(false);
    }
  };

  const handleCancel = () => {
    form.setFieldValue("note", call.note ?? "");
    setEditingNote(false);
  };

  return (
    <>
      <div className="space-y-2 border border-gray-200 rounded-lg bg-white">
        <div className="flex items-center justify-between p-4 bg-gray-100 border-b border-b-gray-200">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Voicemail className="h-4 w-4 text-gray-600 flex-shrink-0" />
            <h3 className="text-sm font-medium text-gray-900 truncate">{t("Voicemail Details")}</h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
            <p className="font-medium text-blue-600">{t("Duration: {{duration}}", { duration })}</p>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {!!call.recordingURI && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col gap-4">
              <AudioPlayer src={call.recordingURI} durationOverride={call.recordingDurationInSeconds ?? undefined} />
              {!!call.transcription && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-gray-900">{t("Voicemail Transcript:")}</h4>
                  <p className="text-xs text-gray-500">{call.transcription}</p>
                </div>
              )}
            </div>
          )}
          <div className="border border-gray-200 rounded-lg">
            <div className="border-b p-3 flex justify-between items-center h-[48px]">
              <h3 className="text-sm font-medium text-gray-900">{t("Voicemail Notes")}</h3>
              {!editingNote && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingNote(true);
                    setTimeout(() => {
                      focusTextarea();
                    });
                  }}
                >
                  <Pencil className="size-4" />
                </Button>
              )}
            </div>
            <div className="max-h-[200px] overflow-y-auto p-3">
              {editingNote ? (
                <Textarea
                  {...form.getFieldProps("note")}
                  ref={noteTextareaRef}
                  onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                    e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length);
                  }}
                />
              ) : (
                <p className={`text-sm ${call.note ? "text-gray-900" : "text-gray-500"}`}>
                  {call.note || t("No notes added yet")}
                </p>
              )}
            </div>
          </div>

          {!!call.tags?.length && (
            <div className="flex gap-2 pt-2">
              {call.tags.map((tag, i) => (
                <Badge key={`${tag}-${i}`} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {editingNote && (
        <TimelineBar
          title={t("Editing voicemail note")}
          subtext={t("Voicemail")}
          primaryAction={{
            label: t("Save"),
            onClick: handleSave,
          }}
          secondaryActions={[
            {
              label: t("Cancel"),
              onClick: handleCancel,
            },
          ]}
          className="absolute"
        />
      )}
    </>
  );
}
